import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Blocks, CircleAlert, Pencil, Plug, Plus, Power, PowerOff, Trash2, Zap } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { McpServer } from "@/api/generated/McpServer"
import { mcpKeys, useDeleteMcp, useMcpServers, useTestMcp, useUpdateMcp } from "@/api/queries/mcp"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { McpBadges } from "@/features/studio/agent-editor/tools-tab"
import { fire } from "@/features/studio/fire"
import { onOAuthOutcome, parseOAuthOutcome } from "@/features/studio/mcp-oauth"
import { McpSheet } from "@/features/studio/mcp-sheet"
import { formatNumber } from "@/lib/format"

interface McpSearch {
  server?: number
  new?: boolean
  oauth_success?: string
  oauth_error?: string
  popup?: boolean
}

export const Route = createFileRoute("/_app/studio/mcp")({
  staticData: { titleKey: "nav.items.mcp" },
  validateSearch: (s: Record<string, unknown>): McpSearch => ({
    server: Number(s.server) > 0 ? Number(s.server) : undefined,
    new: s.new === true || s.new === "true" ? true : undefined,
    oauth_success: s.oauth_success != null && s.oauth_success !== "" ? String(s.oauth_success) : undefined,
    oauth_error: s.oauth_error != null && s.oauth_error !== "" ? String(s.oauth_error) : undefined,
    popup: s.popup === true || s.popup === "true" ? true : undefined,
  }),
  component: McpPage,
})

function McpPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const servers = useMcpServers()
  const update = useUpdateMcp()
  const remove = useDeleteMcp()
  const test = useTestMcp()
  const confirm = useConfirm()
  const [testing, setTesting] = useState<number>()

  const setSearch = (patch: Partial<McpSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // OAuth result arriving via redirect (`/studio/integrations` → here) or from a popup
  // biome-ignore lint/correctness/useExhaustiveDependencies: handle once per outcome
  useEffect(() => {
    const outcome = parseOAuthOutcome(search)
    if (!outcome) return
    void qc.invalidateQueries({ queryKey: mcpKeys.all })
    const name = servers.data?.find((s) => s.id === outcome.serverId)?.name ?? ""
    if (outcome.success) toast.success(t("studio.mcp.oauth.success", { name }))
    else toast.error(t("studio.mcp.oauth.error"), { description: outcome.error })
    setSearch({
      oauth_success: undefined,
      oauth_error: undefined,
      popup: undefined,
      server: outcome.success && !search.popup ? outcome.serverId : search.server,
    })
  }, [search.oauth_success, search.oauth_error])

  // outcomes from popups while no editor is open
  useEffect(
    () =>
      onOAuthOutcome(() => {
        void qc.invalidateQueries({ queryKey: mcpKeys.all })
      }),
    [qc],
  )

  const editing = servers.data?.find((s) => s.id === search.server)
  const sheetOpen = !!search.new || (search.server != null && !!editing)

  const runTest = async (s: McpServer) => {
    setTesting(s.id)
    try {
      const r = await test.mutateAsync(s.id)
      if (r.ok)
        toast.success(t("studio.mcp.test.okNamed", { name: s.name, count: r.tools.length, ms: r.latency_ms }))
      else
        toast.error(t("studio.mcp.test.failedNamed", { name: s.name }), {
          description: r.needs_authorization ? t("studio.mcp.test.needsAuth") : (r.error ?? undefined),
        })
    } finally {
      setTesting(undefined)
    }
  }

  const columns = useMemo(
    () =>
      createColumns<McpServer>((c) => [
        c.accessor("name", {
          header: t("studio.mcp.columns.integration"),
          enableHiding: false,
          cell: ({ row }) => {
            const s = row.original
            const Icon = s.transport === "builtin" ? Blocks : Plug
            return (
              <div className="flex max-w-md min-w-56 items-center gap-3">
                <div className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{s.name}</span>
                    {s.tool_prefix && (
                      <code className="text-muted-foreground font-mono text-[0.7rem]">{s.tool_prefix}__</code>
                    )}
                  </div>
                  <div className="text-muted-foreground truncate text-xs">
                    {s.description ||
                      (s.transport === "stdio" ? `${s.command} ${s.args.join(" ")}` : s.url) ||
                      "—"}
                  </div>
                </div>
              </div>
            )
          },
        }),
        c.display({
          id: "type",
          header: t("studio.mcp.columns.type"),
          meta: { label: t("studio.mcp.columns.type") },
          cell: ({ row }) => (
            <div className="flex flex-wrap gap-1">
              {/* The Status column already shows Disabled; repeating it here widens this column. */}
              <McpBadges server={row.original} showDisabled={false} />
            </div>
          ),
        }),
        c.accessor("auth_type", {
          header: t("studio.mcp.columns.auth"),
          cell: ({ getValue }) => (
            <span className="text-muted-foreground text-xs">
              {t(`studio.mcp.auth.${getValue()}` as "studio.mcp.auth.none", getValue())}
            </span>
          ),
        }),
        c.accessor((s) => (s.last_error ? 0 : s.enabled ? 2 : 1), {
          id: "status",
          header: t("studio.mcp.columns.status"),
          meta: { label: t("studio.mcp.columns.status") },
          cell: ({ row }) => {
            const s = row.original
            if (s.last_error)
              return (
                <Tooltip>
                  <TooltipTrigger render={<span />}>
                    <StatusBadge tone="danger">
                      <CircleAlert />
                      {t("studio.mcp.status.error")}
                    </StatusBadge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-80 break-words">{s.last_error}</TooltipContent>
                </Tooltip>
              )
            return s.enabled ? (
              <StatusBadge tone="success" dot>
                {t("studio.mcp.status.enabled")}
              </StatusBadge>
            ) : (
              <StatusBadge>{t("studio.mcp.status.disabled")}</StatusBadge>
            )
          },
        }),
        c.accessor((s) => s.tool_count ?? -1, {
          id: "tools",
          header: t("studio.mcp.columns.tools"),
          meta: { label: t("studio.mcp.columns.tools"), align: "end" },
          cell: ({ row }) => {
            const s = row.original
            if (s.tool_count == null) return <span className="text-muted-foreground text-xs">—</span>
            return (
              <span className="tabular-nums">
                {s.enabled_tools ? `${s.enabled_tools.length}/${s.tool_count}` : formatNumber(s.tool_count)}
              </span>
            )
          },
        }),
        c.accessor("agent_count", {
          header: t("studio.mcp.columns.agents"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.mcp.title")}
        description={t("studio.mcp.description")}
        icon={Plug}
        actions={
          <Button onClick={() => setSearch({ new: true, server: undefined })} data-testid="new-mcp">
            <Plus />
            {t("studio.mcp.new")}
          </Button>
        }
      />
      <DataTable
        label={t("studio.mcp.title")}
        data={servers.data}
        columns={columns}
        getRowId={(s) => String(s.id)}
        loading={servers.isPending}
        error={servers.error}
        onRetry={() => void servers.refetch()}
        searchable
        searchPlaceholder={t("studio.mcp.search")}
        initialSorting={[{ id: "name", desc: false }]}
        columnToggle
        persistKey="studio-mcp"
        onRowClick={(s) => setSearch({ server: s.id, new: undefined })}
        isRowActive={(s) => s.id === search.server}
        rowActions={(s) => (
          <>
            <DropdownMenuItem onClick={() => fire(runTest(s))} disabled={testing === s.id}>
              <Zap />
              {t("studio.mcp.test.action")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSearch({ server: s.id, new: undefined })}>
              <Pencil />
              {t("common.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => update.mutate({ id: s.id, enabled: !s.enabled })}
              disabled={s.transport === "stdio" && update.isPending}
            >
              {s.enabled ? <PowerOff /> : <Power />}
              {s.enabled ? t("studio.mcp.disable") : t("studio.mcp.enable")}
            </DropdownMenuItem>
            {!s.is_system && s.transport !== "builtin" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() =>
                    void confirm({
                      title: t("studio.mcp.deleteTitle", { name: s.name }),
                      description:
                        s.agent_count > 0
                          ? t("studio.mcp.deleteInUse", { count: s.agent_count })
                          : t("studio.mcp.deleteDescription"),
                      confirmLabel: t("studio.mcp.delete"),
                      destructive: true,
                      onConfirm: () => remove.mutateAsync(s),
                    })
                  }
                >
                  <Trash2 />
                  {t("studio.mcp.delete")}
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Plug}
            title={t("studio.mcp.empty.title")}
            description={t("studio.mcp.empty.description")}
            action={
              <Button size="sm" onClick={() => setSearch({ new: true })}>
                <Plus />
                {t("studio.mcp.new")}
              </Button>
            }
          />
        }
      />
      <McpSheet
        open={sheetOpen}
        server={search.new ? undefined : editing}
        onOpenChange={(o) => !o && setSearch({ server: undefined, new: undefined })}
        onCreated={(s) => setSearch({ new: undefined, server: s.id })}
      />
    </PageContainer>
  )
}
